import React from "react";
import { AlertTriangle, ArrowLeft, ArrowRight } from "lucide-react";

/**
 * ClashWarningDialog — presents an unavoidable choice when saving an
 * appointment would double-book one or more engineers.
 *
 * Two outcomes are offered:
 *   • "Return and amend" — closes the dialog; the parent form stays intact
 *     so the user can pick a different time or engineer.
 *   • "Continue despite clash" — resolves the promise with `overridden=true`
 *     so the parent can re-submit with `clash_override=true`.
 *
 * The clash object shape is what the backend returns from
 * `POST /api/calendar/check-clash` OR from the `detail` of a 409 error.
 */
export default function ClashWarningDialog({ open, clashes = [], onCancel, onOverride }) {
    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center px-4"
            data-testid="clash-dialog"
            role="alertdialog"
            aria-labelledby="clash-title"
        >
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#DA3535]/12 text-[#DA3535] flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 id="clash-title" className="text-lg font-semibold text-[#131B2F]">Scheduling clash</h3>
                        <p className="text-sm text-[#4B5565] mt-1">
                            {clashes.length === 1
                                ? "One engineer is already booked during this window."
                                : `${clashes.length} bookings overlap with the selected time.`}
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
                    {clashes.map((c, i) => (
                        <div
                            key={`${c.appointment_id}-${i}`}
                            className="border border-[#DA3535]/25 bg-[#FEF3F3] rounded-lg px-4 py-3 text-sm text-[#131B2F]"
                            data-testid={`clash-row-${i}`}
                        >
                            <div className="font-medium">
                                {c.engineer_name || "Unknown engineer"} is already assigned
                            </div>
                            <div className="text-[#4B5565] mt-1">
                                {c.customer_name || "Untitled project"}
                                {c.appointment_type ? ` — ${c.appointment_type}` : ""}
                                {c.arrival_window ? `, ${c.arrival_window}` : ""}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="px-6 py-4 border-t border-[#E5E7EB] flex flex-wrap justify-end gap-2">
                    <button
                        onClick={onCancel}
                        data-testid="clash-cancel"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium text-[#131B2F] hover:bg-[#F5F5F7] transition-colors"
                    >
                        <ArrowLeft size={14} strokeWidth={1.75} /> Return and amend
                    </button>
                    <button
                        onClick={onOverride}
                        data-testid="clash-override"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#DA3535] text-white text-sm font-medium hover:bg-[#B92C2C] transition-colors"
                    >
                        Continue despite clash <ArrowRight size={14} strokeWidth={1.75} />
                    </button>
                </div>
            </div>
        </div>
    );
}
